import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { CalendarDays, CheckCircle2, CircleDollarSign, Clock3, ReceiptText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { buildCollaboratorFinance, getDateValue } from '../../utils/collaboratorFinance';
import LoadingState from '../shared/LoadingState';
import './CollaboratorFinance.css';

const CollaboratorFinance = () => {
  const { userData } = useAuth();
  const companyId = userData?.currentCompanyId || userData?.companyId;
  const email = userData?.email || '';
  const [reservations, setReservations] = useState([]);
  const [services, setServices] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!companyId || !email) return undefined;
    let reservationsReady = false;
    let servicesReady = false;
    let entriesReady = false;
    const finish = () => reservationsReady && servicesReady && entriesReady && setLoading(false);
    const unsubscribeReservations = onSnapshot(
      query(collection(db, 'companies/' + companyId + '/reservations'), where('createdByEmail', '==', email)),
      snapshot => {
        setReservations(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
        reservationsReady = true;
        finish();
      },
      error => {
        console.error('Erro ao acompanhar reservas financeiras:', error);
        reservationsReady = true;
        finish();
      }
    );
    const unsubscribeServices = onSnapshot(
      collection(db, 'companies/' + companyId + '/services'),
      snapshot => {
        setServices(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
        servicesReady = true;
        finish();
      },
      error => {
        console.error('Erro ao acompanhar serviços financeiros:', error);
        servicesReady = true;
        finish();
      }
    );
    const unsubscribeEntries = onSnapshot(
      query(
        collection(db, 'companies/' + companyId + '/financialEntries'),
        where('collaboratorEmail', '==', email),
        where('type', '==', 'collaborator_payout')
      ),
      snapshot => {
        setEntries(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
        entriesReady = true;
        finish();
      },
      error => {
        console.error('Erro ao acompanhar repasses:', error);
        entriesReady = true;
        finish();
      }
    );
    return () => {
      unsubscribeReservations();
      unsubscribeServices();
      unsubscribeEntries();
    };
  }, [companyId, email]);

  const finance = useMemo(() => buildCollaboratorFinance({
    reservations, services, entries, collaboratorEmail: email, startDate, endDate
  }), [endDate, entries, email, reservations, services, startDate]);
  const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  const date = value => getDateValue(value)?.toLocaleDateString('pt-BR') || '-';

  if (loading) return <LoadingState title="Carregando seu financeiro" subtitle="Calculando comissões e repasses" />;

  return <main className="collaborator-finance">
    <header className="collaborator-finance__header">
      <div><h1>Meu financeiro</h1><p>Comissões de reservas com pagamento e check-in confirmados</p></div>
      <div className="collaborator-finance__dates">
        <label>De<input type="date" value={startDate} max={endDate || undefined} onChange={event => setStartDate(event.target.value)} /></label>
        <label>Até<input type="date" value={endDate} min={startDate || undefined} onChange={event => setEndDate(event.target.value)} /></label>
        {(startDate || endDate) && <button type="button" onClick={() => { setStartDate(''); setEndDate(''); }}>Limpar</button>}
      </div>
    </header>

    <section className="collaborator-finance__summary">
      <div><CircleDollarSign /><span>Comissão gerada</span><strong>{money(finance.generated)}</strong></div>
      <div className="is-paid"><CheckCircle2 /><span>Já recebido</span><strong>{money(finance.paid)}</strong></div>
      <div className="is-pending"><Clock3 /><span>A receber</span><strong>{money(finance.pending)}</strong></div>
      <div><CalendarDays /><span>Reservas elegíveis</span><strong>{finance.reservations.length}</strong></div>
    </section>

    <section className="collaborator-finance__section">
      <div className="collaborator-finance__title"><div><span>Origem das comissões</span><h2>Reservas confirmadas</h2></div><ReceiptText /></div>
      {finance.reservations.length === 0 ? <p className="collaborator-finance__empty">Nenhuma reserva com pagamento e check-in confirmados no período.</p> :
        <div className="collaborator-finance__table-wrap"><table>
          <thead><tr><th>Data</th><th>Cliente</th><th>Serviço</th><th>Reserva</th><th>Comissão</th><th>Repasse automático</th></tr></thead>
          <tbody>{finance.reservations.map(item => <tr key={item.id}>
            <td data-label="Data">{date(item.date)}</td><td data-label="Cliente">{item.clientName || '-'}</td><td data-label="Serviço">{item.serviceName || '-'}</td>
            <td data-label="Reserva">{money(item.totalAmount)}</td><td data-label="Comissão">{item.commissionRate}% · <strong>{money(item.commissionAmount)}</strong></td>
            <td data-label="Repasse automático">{money(item.automaticPayout)}</td>
          </tr>)}</tbody>
        </table></div>}
    </section>

    <section className="collaborator-finance__section">
      <div className="collaborator-finance__title"><div><span>Histórico</span><h2>Pagamentos recebidos</h2></div><CheckCircle2 /></div>
      {finance.entries.length === 0 && finance.automaticPaid === 0 ? <p className="collaborator-finance__empty">Nenhum repasse registrado no período.</p> :
        <>
          <div className="collaborator-finance__payment-list">{[...finance.entries, ...finance.automaticEntries]
            .sort((a, b) => (getDateValue(b.date)?.getTime() || 0) - (getDateValue(a.date)?.getTime() || 0))
            .map(entry => <article key={entry.id}>
            <div><strong>{entry.description || 'Repasse de comissão'}</strong><span>{date(entry.date)} · {entry.methodLabel || entry.method || 'Não informado'}</span></div>
            <strong>{money(entry.amount)}</strong>
          </article>)}</div>
        </>}
    </section>
  </main>;
};

export default CollaboratorFinance;
